import { isObject } from 'radash';
import { CompositionConfig } from '../types/RemotionPlayerConfigTypes/CompositionConfig';
import { TrackType } from '../types/Track/Type';
import { ImageElement } from '../types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { TransitionLifeCycleType } from '../types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.types';
import { Clip } from '../types/clip-types';
import { getAllCompositeSegments, getCompositeSegmentById, isSplitsInTimelineEnabledPure } from './clipUtils';

type PlayerConfigType = Pick<
  CompositionConfig,
  'playerConfig' | 'fps' | 'durationInFrames' | 'height' | 'width' | 'version'
>;

type AcceptablePlayerConfigInput = PlayerConfigType | null | undefined;

export const isPlayerConfigValid = (playerConfig: AcceptablePlayerConfigInput): playerConfig is PlayerConfigType => {
  return isObject(playerConfig);
};

export const getPlayerConfigTracks = (playerConfig: AcceptablePlayerConfigInput) => {
  if (isPlayerConfigValid(playerConfig)) {
    return playerConfig.playerConfig.tracks;
  }
  return null;
};

export const getPlayerConfigElementTracks = (playerConfig: AcceptablePlayerConfigInput) => {
  if (isPlayerConfigValid(playerConfig)) {
    return getPlayerConfigTracks(playerConfig)?.filter(track => track.type === TrackType.ELEMENTS) ?? null;
  }
  return null;
};

export const getAllElementTrackItems = (playerConfig: AcceptablePlayerConfigInput) => {
  if (isPlayerConfigValid(playerConfig)) {
    return getPlayerConfigElementTracks(playerConfig)?.flatMap(track => track.elements) ?? null;
  }
  return null;
};

export const getPlayerConfigElementById = (playerConfig: AcceptablePlayerConfigInput, elementId: string) => {
  if (isPlayerConfigValid(playerConfig)) {
    return getAllElementTrackItems(playerConfig)?.find(e => e.id === elementId) ?? null;
  }
  return null;
};

export const getElementTransitionByLifeCycleType = (
  element: ImageElement | VideoElement,
  lifeCycle: TransitionLifeCycleType
) => {
  if (lifeCycle === TransitionLifeCycleType.IN) {
    return element.transitions?.in ?? null;
  }
  return element.transitions?.out ?? null;
};

export const getPlayerConfigMetadata = (playerConfig: AcceptablePlayerConfigInput) => {
  if (isPlayerConfigValid(playerConfig)) {
    return playerConfig.playerConfig.metadata;
  }
  return undefined;
};
/**
 * Retrieves captions for a specific segment or the entire clip.
 *
 * When splits are enabled and the clip has multiple segments, this function returns captions
 * from the segment's metadata. Otherwise, it returns captions from the clip's asset metadata.
 *
 * @param clip - The clip to retrieve captions from
 * @param segmentId - Optional segment ID. Required when splits are enabled and multiple segments exist
 * @returns An array of caption words (Word[]) for the specified segment, or the clip's captions if splits are disabled
 * @throws {Error} If splits are enabled, multiple segments exist, and no segmentId is provided
 *
 * @remarks
 * - Returns clip-level captions when splits are not enabled or player config is missing
 * - When splits are enabled with a single segment, returns captions from the first segment
 * - When splits are enabled with multiple segments, segmentId is required to identify the specific segment
 * - Returns an empty array if segment metadata captions are not found
 */

export const getCaptionsForSegment = ({
  clip,
  segmentId,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  segmentId?: string;
  isSplitsTranslationEnabled: boolean;
}) => {
  const splitsEnabled = isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled });
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config || !clip.asset_metadata.player_config.playerConfig) {
    return clip.asset_metadata.captions;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.captions || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.captions || [];
};
